/**
 * ChatBloc Dependencies Interface
 *
 * Defines all external dependencies that ChatBloc needs to function.
 * This enables dependency injection for testing and module extraction.
 */

import type {
  WebsocketMessage,
  ObserverCallback
} from "src/state/WebSocketBloc/WebSocketBloc";
import WebSocketBloc from "src/state/WebSocketBloc/WebSocketBloc";
import { Blac } from "blac-next";
import { GreenCircleIcon } from "src/constants/icons";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import {
  authenticationState,
  userState,
  appViewState,
  toast,
  fileState
} from "src/state/state";

/**
 * External dependencies that ChatBloc needs to function.
 */
export interface ChatBlocDependencies {
  // Storage operations
  storage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };

  // WebSocket operations
  websocket: {
    send: (message: WebsocketMessage) => Promise<void>;
    addObserver: (type: string, handler: ObserverCallback) => void; // Type is relaxed to string for flexibility
    removeLocalMessage: (message: WebsocketMessage) => void;
    getConnectionState: () => number | undefined;
  };

  // Authentication
  auth: {
    getAccessToken: () => string | null;
  };

  // User state
  user: {
    hasAppAccess: () => boolean;
  };

  // App view state
  app: {
    isInApp: () => boolean;
  };

  // UI notifications
  notifications: {
    showError: (
      message: string,
      params?: Record<string, string | number>
    ) => void;
    showResponseTimeToast: () => void;
  };

  // File operations
  files: {
    uploadFile: (options: {
      file: File;
    }) => Promise<{ id: string } | undefined>;
  };
}

/**
 * Creates dependencies using the global singleton instances.
 * This is the default behavior that maintains backward compatibility.
 */
export function createDefaultChatBlocDependencies(): ChatBlocDependencies {
  const websocketState = Blac.getBloc(WebSocketBloc);

  return {
    storage: {
      getItem: (key) => StorageController.getItem(key),
      setItem: (key, value) => StorageController.setItem(key, value)
    },

    websocket: {
      send: (message) => websocketState.send(message),
      addObserver: (type, handler) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
        websocketState.addObserver(type as any, handler),
      removeLocalMessage: (message) =>
        websocketState.removeLocalMessage(message),
      getConnectionState: () => websocketState.websocket?.readyState
    },

    auth: {
      getAccessToken: () => authenticationState.accessToken ?? null
    },

    user: {
      hasAppAccess: () => userState.hasAppAccess()
    },

    app: {
      isInApp: () => appViewState.state.inApp
    },

    notifications: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      showError: (message, params) => toast.error(message as any, params),
      showResponseTimeToast: () =>
        toast.show("chat.responseTime", {
          duration: 0,
          icon: GreenCircleIcon,
          cssClass: "toast-transparent toast-chat-response-time"
        })
    },

    files: {
      uploadFile: (options) => fileState.uploadFile(options)
    }
  };
}
